import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import DataComponent from '../../../DataComponent/DataComponent';
import CustomTooltip from '../../../Tooltip';

const images = {
    x: require('shared_img/icons/x.svg'),
};

const minCountExtraParams = 1;

const PostbackParameters = ({ 
    headerNames = [], 
    errors = [], 
    data = {
        list: [],
        selected: [],
        extra: []
    }, 
    loading = false, 
    getPostbackParameters, 
    onAction, 
    onValidate,
    t, 
    ...props 
}) => {
    const [state, setState] = useState({
        extra: data.extra,
        list: data.list,
        selected: data.selected,
        validationErrors: {}
    });

    const hasContent = useCallback((item = {}) => {
        return !!((item.name && item.name.trim()) || (item.value && item.value.trim()));
    }, []);

    const ensureBlankRow = useCallback((list = []) => {
        if (!list.length) {
            list.push({ name: '', value: '' });
            return list;
        }
        const hasEmpty = list.some(item => !hasContent(item));
        if (!hasEmpty) {
            list.push({ name: '', value: '' });
        }
        return list;
    }, [hasContent]);

    const onChange = useCallback(() => {
        setState(prevState => {
            let selected = [];

            prevState.list.map((item) => {
                if (item.checked) {
                    selected.push({
                        name: item.data.name,
                        value: item.data.macros
                    });
                }
            });

            prevState.extra.map((item) => {
                if (item.name && item.value) {
                    selected.push(item);
                }
            });

            if (onAction) {
                onAction({
                    selected: selected,
                    extra: prevState.extra,
                    list: prevState.list
                });
            }

            return prevState;
        });
    }, [onAction, ensureBlankRow]);

    const onDataTableAction = useCallback((state, actionType) => {
        // Empty implementation
    }, []);

    const unique = useCallback((extra = [], selected = []) => {
        extra.map((itemExtra) => {
            if (!itemExtra.name || !itemExtra.value) return;

            const index = selected.findIndex((item) => itemExtra.value === item.value);

            if (index === -1) {
                selected.push(itemExtra);
            }
        });
    }, []);

    const prevDataSignatureRef = useRef(null);

    const updateSelected = useCallback(() => {
        setState(prevState => {
            let selected = [];

            prevState.list.map((item) => {
                if (item.checked) {
                    selected.push({
                        name: item.data.name,
                        value: item.data.macros
                    });
                }
            });

            prevState.extra.map((item) => {
                if (item.name && item.value) {
                    selected.push(item);
                }
            });

            const newState = {
                ...prevState,
                selected
            };
            
            if (onAction) {
                onAction({
                    selected: selected,
                    extra: prevState.extra,
                    list: prevState.list
                });
            }
            
            return newState;
        });
    }, [onAction, ensureBlankRow]);

    const onChangeField = useCallback((event, index, keyState) => {
        setState(prevState => {
            let list = [...prevState[keyState]];
            const name = event.target.name;

            if (keyState === 'list') {
                list[index].data[name] = event.target.value;
            } else {
                list[index][name] = event.target.value;
                list = ensureBlankRow(list);
            }

            const newState = {
                ...prevState,
                [keyState]: list
            };

            if (keyState === 'extra') {
                let selected = [];
                prevState.list.map((item) => {
                    if (item.checked) {
                        selected.push({
                            name: item.data.name,
                            value: item.data.macros
                        });
                    }
                });
                list.map((item) => {
                    if (item.name && item.value) {
                        selected.push(item);
                    }
                });
                newState.selected = selected;

                if (onAction) {
                    onAction({
                        selected: selected,
                        extra: list,
                        list: prevState.list
                    });
                }
            }

            return newState;
        });
    }, [onAction, ensureBlankRow]);

    const onChangeSelected = useCallback((event) => {
        const checked = event.target.checked;
        const index = event.target.value;
        
        setState(prevState => {
            let list = [...prevState.list];
            list[index].checked = checked;
            
            const newState = {
                ...prevState,
                list
            };
            
            let selected = [];
            list.map((item) => {
                if (item.checked) {
                    selected.push({
                        name: item.data.name,
                        value: item.data.macros
                    });
                }
            });
            prevState.extra.map((item) => {
                if (item.name && item.value) {
                    selected.push(item);
                }
            });
            
            newState.selected = selected;
            
            if (onAction) {
                onAction({
                    selected: selected,
                    extra: prevState.extra,
                    list: list
                });
            }
            
            return newState;
        });
    }, [onAction]);

    const onAddParameter = useCallback(() => {
        setState(prevState => {
            let list = [...prevState.extra];
            list.push({
                name: '',
                value: ''
            });
            
            const newState = {
                ...prevState,
                extra: list
            };
            
            if (onAction) {
                onAction({
                    selected: prevState.selected,
                    extra: list,
                    list: prevState.list
                });
            }
            
            return newState;
        });
    }, [onAction]);

    const onDeleteParameter = useCallback((index) => {
        setState(prevState => {
            let list = [...prevState.extra];
            
            list.splice(index, 1);
            ensureBlankRow(list);
            
            const newState = {
                ...prevState,
                extra: list
            };
            
            let selected = [];
            prevState.list.map((item) => {
                if (item.checked) {
                    selected.push({
                        name: item.data.name,
                        value: item.data.macros
                    });
                }
            });
            list.map((item) => {
                if (item.name && item.value) {
                    selected.push(item);
                }
            });
            
            newState.selected = selected;
            
            if (onAction) {
                onAction({
                    selected: selected,
                    extra: list,
                    list: prevState.list
                });
            }
            
            return newState;
        });
    }, [onAction, ensureBlankRow]);

    const getParameters = useCallback(() => {
        let list = [];

        state.list.map((item, i) => {
            list.push({
                checked: (
                    <label className="option">
                        <input
                            className="option__input"
                            type="checkbox"
                            name={item.data.name}
                            value={i}
                            checked={item.checked}
                            onChange={onChangeSelected}
                        />
                        <span className="option__mark" />
                        <span className="option__caption" />
                    </label>
                ),
                variableName: (
                    <input
                        className="form-control"
                        type="text"
                        name="name"
                        value={item.data.name}
                        onChange={(event) => { onChangeField(event, i, 'list'); }}
                    />
                ),
                valueVariable: item.data.title,
                macros: item.data.macros
            });
        });

        return list;
    }, [state.list, onChangeSelected, onChangeField]);

    const getExtraParameters = useCallback(() => {
        let list = [];
        const newBtn = (
            <button className="addButton" onClick={onAddParameter} type="button">
                <img src={images.x} alt="" />
            </button>
        );
        const staticNameActions = (t && t.actions) || {};
        const staticNames = (t && t.postbackPage && t.postbackPage.creatingEditing) || {};

        const visibleExtra = state.extra.reduce((acc, item, i) => {
            const isVisible = i === 0 || i < state.extra.length - 1 || hasContent(item);
            if (isVisible) {
                acc.push({ item, index: i });
            }
            return acc;
        }, []);
        const blankVisibleCount = visibleExtra.reduce((count, { item }) => (
            hasContent(item) ? count : count + 1
        ), 0);

        visibleExtra.forEach(({ item, index: i }) => {
            const nameError = state.validationErrors[`extra_${i}_name`];
            const valueError = state.validationErrors[`extra_${i}_value`];
            const itemHasContent = hasContent(item);
            const isDeleteDisabled = !itemHasContent && blankVisibleCount <= 1;
            
            list.push({
                title: <h3 className="bold subtitle"> {staticNames.paramExtraTitle} </h3>
            });
            list.push({
                variableName: (
                    <div>
                        <input
                            className={`form-control ${nameError ? 'error-field' : ''}`}
                            type="text"
                            name="name"
                            value={item.name}
                            data-param-index={`extra_${i}_name`}
                            onChange={(event) => { 
                                onChangeField(event, i, 'extra');

                                if (state.validationErrors[`extra_${i}_name`]) {
                                    setState(prevState => ({
                                        ...prevState,
                                        validationErrors: {
                                            ...prevState.validationErrors,
                                            [`extra_${i}_name`]: undefined
                                        }
                                    }));
                                }
                            }}
                        />
                        {nameError && (
                            <div className="error-message" style={{ color: '#d9534f', fontSize: '12px', marginTop: '4px' }}>
                                {nameError}
                            </div>
                        )}
                    </div>
                ),
                valueVariable: (
                    <div>
                        <input
                            className={`form-control ${valueError ? 'error-field' : ''}`}
                            type="text"
                            name="value"
                            value={item.value}
                            data-param-index={`extra_${i}_value`}
                            onChange={(event) => { 
                                onChangeField(event, i, 'extra');
                                if (state.validationErrors[`extra_${i}_value`]) {
                                    setState(prevState => ({
                                        ...prevState,
                                        validationErrors: {
                                            ...prevState.validationErrors,
                                            [`extra_${i}_value`]: undefined
                                        }
                                    }));
                                }
                            }}
                        />
                        {valueError && (
                            <div className="error-message" style={{ color: '#d9534f', fontSize: '12px', marginTop: '4px' }}>
                                {valueError}
                            </div>
                        )}
                    </div>
                ),
                add: newBtn,
                checked: (
                    <div className="row-controls">
                        <CustomTooltip id={`controlDelete-${i}`} tooltip={staticNameActions.delete}>
                            <a
                                className={`control-item deleteButton${isDeleteDisabled ? ' disabled' : ''}`}
                                href="#!"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (isDeleteDisabled) return;
                                    onDeleteParameter(i);
                                }}
                                style={isDeleteDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : { opacity: 1, cursor: 'pointer' }}
                            >
                                <img src={images.x} alt="" />
                            </a>
                        </CustomTooltip>
                    </div>
                )
            });
        });
        return list;
    }, [state.extra, onAddParameter, onChangeField, onDeleteParameter, t, state.validationErrors]);

    useEffect(() => {
        if (!data.list.length && getPostbackParameters) {
            getPostbackParameters();
        }
    }, [data.list.length, getPostbackParameters]);

    useEffect(() => {
        const dataSignature = JSON.stringify({
            list: data.list,
            selected: data.selected,
            extra: data.extra
        });

        if (prevDataSignatureRef.current === dataSignature) {
            return;
        }

        prevDataSignatureRef.current = dataSignature;

        setState(prevState => {
            const useExisting = Array.isArray(data.extra) && data.extra.length > 0;
            const count = Math.max(
                data.extra.length,
                useExisting ? prevState.extra.length : minCountExtraParams
            );
            const extra = [];

            for (let i = 0; i < count; i++) {
                if (data.extra[i]) {
                    extra.push({ ...data.extra[i] });
                } else if (useExisting && prevState.extra[i]) {
                    extra.push(prevState.extra[i]);
                } else {
                    extra.push({ name: '', value: '' });
                }
            }

            const selected = [...data.selected];
            unique(extra, selected);

            return {
                ...prevState,
                list: data.list,
                selected,
                extra: ensureBlankRow(extra)
            };
        });
    }, [data.list, data.selected, data.extra, unique, ensureBlankRow]);

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        updateSelected();
    }, [state.extra, state.list, updateSelected]);

    const validateParameters = useCallback((options = {}) => {
        const staticNames = (t && t.postbackPage && t.postbackPage.creatingEditing) || {};
        const validationErrors = {};
        let hasErrors = false;

        state.extra.forEach((item, index) => {
            const hasName = item.name && item.name.trim();
            const hasValue = item.value && item.value.trim();
            
            if (hasName && !hasValue) {
                validationErrors[`extra_${index}_value`] = staticNames.valueRequired || 'Value is required';
                hasErrors = true;
            }
            if (hasValue && !hasName) {
                validationErrors[`extra_${index}_name`] = staticNames.nameRequired || 'Name is required';
                hasErrors = true;
            }
        });

        setState(prevState => ({
            ...prevState,
            validationErrors
        }));

        if (hasErrors && !options.silent) {
            setTimeout(() => {
                const firstErrorKey = Object.keys(validationErrors)[0];
                if (firstErrorKey) {
                    const errorElement = document.querySelector(`[data-param-index="${firstErrorKey}"]`);
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        errorElement.focus();
                    }
                }
            }, 100);
        }

        return {
            isValid: !hasErrors,
            errors: validationErrors
        };
    }, [state.extra, t]);


    useEffect(() => {
        if (onValidate) {
            onValidate(validateParameters);
        }
    }, [validateParameters, onValidate]);

    const staticNames = (t && t.postbackPage && t.postbackPage.creatingEditing) || {};
    const staticNameActions = (t && t.actions) || {};

    return (
        <div className="postback__table">
            {(errors && errors.length > 0)
                ? (
                    <div className="has-error">
                        <div className="control-label" style={{ color: '#d9534f' }}>
                            {Array.isArray(errors) ? errors.join(', ') : staticNames.parameterError}
                        </div>
                        <br/>
                    </div>
                )
                : null
            }
            <DataComponent
                onAction={onDataTableAction}
                headerNames={headerNames}
                data={getParameters()}
                loading={loading}
                totalPages={1}
                className="simple-table"
            />
            <div className="postback__add-param-wrapper">
                <h3 className="bold">{staticNames.paramsExtraTitle}</h3>
            </div>
            {state.extra.length
                ? (<div className="postback__extra">
                    <DataComponent
                        onAction={onDataTableAction}
                        data={getExtraParameters()}
                        totalPages={1}
                    />
                </div>)
                : null
            }
        </div>
    );
};

PostbackParameters.propTypes = {
    headerNames: PropTypes.array,
    errors: PropTypes.array,
    data: PropTypes.shape({
        list: PropTypes.array,
        selected: PropTypes.array,
        extra: PropTypes.array
    }),
    loading: PropTypes.bool,
    getPostbackParameters: PropTypes.func,
    onAction: PropTypes.func,
    onValidate: PropTypes.func,
    t: PropTypes.object
};

export default withTranslation(PostbackParameters);
